import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import {
  Modal,
  TextInput,
  ScrollArea,
  UnstyledButton,
  Group,
  Text,
  ThemeIcon,
  Box,
  Kbd,
  ActionIcon,
  Tooltip,
  Loader,
} from '@mantine/core';
import {
  IconSearch,
  IconStar,
  IconStarFilled,
  IconCornerDownLeft,
  IconBuildingCommunity,
} from '@tabler/icons-react';
import { getNavGroups, type NavLeaf } from './navConfig';
import { useNavPreferences } from '../../hooks/useNavPreferences';
import { api } from '../../lib/api';
import type { HubCustomer } from '../../lib/api/domains/technicianHub';
import styles from './CommandPalette.module.css';

interface CommandPaletteProps {
  opened: boolean;
  onClose: () => void;
  isAdmin: boolean;
  isTeamlead?: boolean;
}

interface Entry extends NavLeaf {
  group: string;
}

/** Ab wie vielen Zeichen die Kundensuche losläuft — darunter ist jeder Treffer Zufall. */
const CUSTOMER_MIN_CHARS = 2;
/** Die Palette ist kein Kunden-Hub: ein paar Treffer zum Draufspringen genügen. */
const CUSTOMER_LIMIT = 6;

/** Eine Zeile der Trefferliste — Navigationsziel oder Kunde, beide per Pfeiltasten erreichbar. */
type Row =
  | { kind: 'nav'; key: string; section: string; entry: Entry }
  | { kind: 'customer'; key: string; section: string; customer: HubCustomer };

export function CommandPalette({ opened, onClose, isAdmin, isTeamlead = false }: CommandPaletteProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { favorites, isFavorite, toggleFavorite, recents } = useNavPreferences();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Alle Ziele inkl. Gruppen-Label (für Breadcrumb + Suche)
  const allEntries = useMemo<Entry[]>(() => {
    return getNavGroups(isAdmin, isTeamlead).flatMap((g) =>
      g.sections.flatMap((s) => s.items.map((item) => ({ ...item, group: g.label })))
    );
  }, [isAdmin, isTeamlead]);

  const leafByPath = useMemo(() => {
    const map = new Map<string, Entry>();
    allEntries.forEach((e) => map.set(e.path, e));
    return map;
  }, [allEntries]);

  // Ergebnisse: bei leerer Suche Favoriten + Verlauf, sonst gefilterte Treffer
  const navResults = useMemo<Entry[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const quick: Entry[] = [];
      const seen = new Set<string>();
      [...favorites, ...recents].forEach((path) => {
        const leaf = leafByPath.get(path);
        if (leaf && !seen.has(path)) {
          seen.add(path);
          quick.push(leaf);
        }
      });
      return quick.length > 0 ? quick : allEntries;
    }
    const terms = q.split(/\s+/);
    return allEntries.filter((e) => {
      const haystack = `${e.label} ${e.group} ${e.keywords ?? ''}`.toLowerCase();
      return terms.every((t) => haystack.includes(t));
    });
  }, [query, allEntries, favorites, recents, leafByPath]);

  const showingQuickAccess = query.trim() === '' && (favorites.length > 0 || recents.length > 0);

  // Kundensuche gegen die lokale Kundentabelle (provider-unabhängig, wie im Kunden-Hub)
  const [debouncedQuery] = useDebouncedValue(query.trim(), 250);
  const customerTerm = debouncedQuery.length >= CUSTOMER_MIN_CHARS ? debouncedQuery : '';
  const { data: customerData, isFetching: customersLoading } = useQuery({
    queryKey: ['paletteCustomers', customerTerm],
    queryFn: () => api.searchHubCustomers(customerTerm, 1, CUSTOMER_LIMIT),
    enabled: opened && customerTerm.length > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  // Beim Tippen hängt die Antwort einen Tastendruck hinterher — dann lieber nichts zeigen
  // als Treffer zum vorherigen Suchbegriff.
  const customers = customerTerm && query.trim() === debouncedQuery ? customerData?.customers ?? [] : [];
  const customerSearchPending =
    query.trim().length >= CUSTOMER_MIN_CHARS && (customersLoading || query.trim() !== debouncedQuery);

  const rows = useMemo<Row[]>(() => {
    const navSection = showingQuickAccess ? 'Schnellzugriff' : 'Seiten';
    const navRows: Row[] = navResults.map((entry) => ({
      kind: 'nav',
      key: `nav:${entry.path}`,
      section: navSection,
      entry,
    }));
    const customerRows: Row[] = customers.map((customer) => ({
      kind: 'customer',
      key: `customer:${customer.id}`,
      section: 'Kunden',
      customer,
    }));
    return [...navRows, ...customerRows];
  }, [navResults, customers, showingQuickAccess]);

  // Auswahl zurücksetzen, wenn sich die Trefferliste ändert
  useEffect(() => {
    setSelected(0);
  }, [query, customerTerm]);

  // Auswahl darf nicht ins Leere zeigen, wenn Kundentreffer nachträglich wegfallen
  useEffect(() => {
    setSelected((i) => (i >= rows.length ? Math.max(rows.length - 1, 0) : i));
  }, [rows.length]);

  // Beim Öffnen: Suche leeren und Fokus setzen
  useEffect(() => {
    if (opened) {
      setQuery('');
      setSelected(0);
      // Fokus nach Mount
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [opened]);

  const openCustomer = (customer: HubCustomer) => {
    onClose();
    // Gleiche Buchführung wie die Auswahl im Kunden-Hub, sonst fehlt der Kunde
    // dort anschließend in „zuletzt besucht".
    void api
      .addRecentHubCustomer(customer.id, customer.name, false, {
        customerNumber: customer.customerNumber ?? null,
        city: customer.city ?? null,
      })
      .then(() => queryClient.invalidateQueries({ queryKey: ['hubRecentCustomers'] }))
      .catch(() => {
        /* Verlauf ist Komfort, kein Grund den Sprung scheitern zu lassen */
      });
    navigate(`/customer-hub?customerId=${encodeURIComponent(customer.id)}`);
  };

  const go = (row: Row | undefined) => {
    if (!row) return;
    if (row.kind === 'customer') {
      openCustomer(row.customer);
      return;
    }
    onClose();
    navigate(row.entry.path);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(rows[selected]);
    }
  };

  const customerSubtitle = (customer: HubCustomer) => {
    const parts = [customer.customerNumber, customer.city].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : 'Kunde';
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size="lg"
      padding={0}
      yOffset="12vh"
      transitionProps={{ duration: 120 }}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      classNames={{ content: styles.modalContent }}
    >
      <Box className={styles.searchRow}>
        <IconSearch size={18} className={styles.searchIcon} />
        <TextInput
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          placeholder="Seite, Aktion oder Kunde suchen …"
          variant="unstyled"
          className={styles.searchInput}
          aria-label="Navigation und Kunden durchsuchen"
        />
        {customerSearchPending && <Loader size="xs" color="gray" />}
        <Kbd>Esc</Kbd>
      </Box>

      <ScrollArea.Autosize mah="50vh" type="scroll">
        {rows.length === 0 && !customerSearchPending ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            Kein Treffer für „{query}"
          </Text>
        ) : (
          <Box pb="xs">
            {rows.map((row, idx) => {
              const sectionHeader = idx === 0 || rows[idx - 1].section !== row.section ? row.section : null;
              const fav = row.kind === 'nav' && isFavorite(row.entry.path);
              const label = row.kind === 'nav' ? row.entry.label : row.customer.name;
              const subtitle = row.kind === 'nav' ? row.entry.group : customerSubtitle(row.customer);
              const RowIcon = row.kind === 'nav' ? row.entry.icon : IconBuildingCommunity;
              return (
                <Box key={row.key}>
                  {sectionHeader && (
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase" px="md" pt="sm" pb={4}>
                      {sectionHeader}
                    </Text>
                  )}
                  <UnstyledButton
                    className={`${styles.row} ${idx === selected ? styles.rowActive : ''}`}
                    onMouseEnter={() => setSelected(idx)}
                    onClick={() => go(row)}
                  >
                    <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                      <ThemeIcon
                        size="sm"
                        variant="default"
                        radius="sm"
                        color={row.kind === 'customer' ? 'orange' : undefined}
                      >
                        <RowIcon size={15} />
                      </ThemeIcon>
                      <Box style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate>
                          {label}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {subtitle}
                        </Text>
                      </Box>
                    </Group>
                    <Group gap={6} wrap="nowrap">
                      {row.kind === 'nav' && (
                        <Tooltip label={fav ? 'Favorit entfernen' : 'Zu Favoriten'} withArrow>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color={fav ? 'yellow' : 'gray'}
                            onClick={(e) => {
                              e.stopPropagation();
                              void toggleFavorite(row.entry.path);
                            }}
                            aria-label={fav ? 'Favorit entfernen' : 'Zu Favoriten hinzufügen'}
                          >
                            {fav ? <IconStarFilled size={14} /> : <IconStar size={14} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {idx === selected && (
                        <IconCornerDownLeft size={14} className={styles.enterHint} />
                      )}
                    </Group>
                  </UnstyledButton>
                </Box>
              );
            })}
            {customerSearchPending && customers.length === 0 && (
              <Text size="xs" c="dimmed" px="md" py="xs">
                Kunden werden gesucht …
              </Text>
            )}
          </Box>
        )}
      </ScrollArea.Autosize>

      <Group justify="space-between" className={styles.footer} px="md" py={6}>
        <Group gap={4}>
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          <Text size="xs" c="dimmed">
            navigieren
          </Text>
        </Group>
        <Group gap={4}>
          <Kbd>↵</Kbd>
          <Text size="xs" c="dimmed">
            öffnen
          </Text>
        </Group>
      </Group>
    </Modal>
  );
}
